import { useEffect, useRef } from 'react';
import { setScreenBrightness } from '../utils/nativeBrightness';
import { onAttentionRequest } from '../services/AttentionBridge';

/**
 * De echte schermverlichting, in de gewone stand en in de slaapstand.
 *
 * Gemeten op de testtablet (2026-09-28): ~1000 mA bij 80% helderheid tegen ~275 mA op het
 * minimum. De slaapstand dimde tot dan met een zwarte CSS-laag, die de verlichting op vol
 * vermogen laat branden — daardoor haalde een tablet de nacht niet.
 *
 * Beide niveaus komen uit de weergave-instellingen, normaal gezet vanuit het portaal
 * (site-standaard, per appartement anders):
 *   awakePct  — gewone stand; 100 = de helderheid van het toestel zelf
 *   sleepPct  — eindstand in de slaapstand
 *
 *   wakker          → awake (na een wijziging vloeiend in 1 s)
 *   slaapstand in   → in 10 s naar een tussenstap (≤ 15%, nooit lager dan sleep)
 *   na 5 minuten    → in 60 s naar sleep
 *   slaapstand uit  → meteen terug naar awake
 *
 * Een melding die de screensaver mag onderbreken (`high`/`urgent`) zet de helderheid
 * meteen terug, ook als de slaapstand blijft staan; na HOLD begint het dimmen opnieuw.
 * Een oproep of noodoproep wekt de slaapstand al; de native `wakeScreen` zet de
 * verlichting daarbij zelf ook terug.
 *
 * Op een APK < 1.0.5 of in de browser doet dit niets: daar dimt de CSS-laag zoals vroeger.
 */
export const STANDBY_DIM_STEP = 0.15;
export const STANDBY_DIM_FADE_MS = 10_000;
export const STANDBY_NIGHT_AFTER_MS = 5 * 60_000;
export const STANDBY_NIGHT_FADE_MS = 60_000;
export const STANDBY_ATTENTION_HOLD_MS = 2 * 60_000;
export const AWAKE_FADE_MS = 1_000;

/** 100% = de helderheid van het toestel (-1 = geen override). */
export function awakeLevel(awakePct: number): number {
  return awakePct >= 100 ? -1 : Math.max(0.05, awakePct / 100);
}

export function useStandbyBrightness(sleepMode: boolean, awakePct = 100, sleepPct = 1) {
  const awake = awakeLevel(awakePct);
  const night = Math.max(0.01, Math.min(1, sleepPct / 100));
  const wasAsleep = useRef(false);

  useEffect(() => {
    if (!sleepMode) {
      // Net wakker: meteen, niet vloeiend — wie aanraakt of gebeld wordt, moet zien.
      // Een gewijzigde instelling terwijl wakker: vloeiend.
      void setScreenBrightness(awake, wasAsleep.current ? 0 : AWAKE_FADE_MS);
      wasAsleep.current = false;
      return undefined;
    }
    wasAsleep.current = true;

    // Nooit feller dan wakker, nooit donkerder dan de eindstand.
    const awakeForCompare = awake < 0 ? 1 : awake;
    const step = Math.max(night, Math.min(awakeForCompare, STANDBY_DIM_STEP));

    let timers: ReturnType<typeof setTimeout>[] = [];
    const clear = () => {
      timers.forEach(clearTimeout);
      timers = [];
    };
    const startDimming = (delayMs: number) => {
      clear();
      timers.push(
        setTimeout(() => void setScreenBrightness(step, STANDBY_DIM_FADE_MS), delayMs),
        setTimeout(() => void setScreenBrightness(night, STANDBY_NIGHT_FADE_MS), delayMs + STANDBY_NIGHT_AFTER_MS),
      );
    };

    startDimming(0);

    const off = onAttentionRequest((request) => {
      if (!request.breakScreensaver && !request.wakeFromSleep) return;
      void setScreenBrightness(awake);
      startDimming(STANDBY_ATTENTION_HOLD_MS);
    });

    // Geen herstel hier: een instelling die 's nachts wijzigt, mag een slapende tablet
    // niet even fel zetten. Het wakker worden zelf herstelt (tak hierboven).
    return () => {
      off();
      clear();
    };
  }, [sleepMode, awake, night]);
}
